import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { TeamService } from './team.service';
import { TeamInterviewerItem, TeamItem, TeamMemberItem, TeamUpsertPayload } from '../../interfaces/team/team.interface';
import { environment } from '../../../../../environments/environment.dev';
import { PageResponse } from '../../../../core/interfaces/page-response.interface';
import { convertToHttpParams } from '../../../../core/interfaces/common-search-param.interface';

@Injectable({
    providedIn: 'root'
})
export class TeamApiService extends TeamService {
    private http = inject(HttpClient);
    private readonly baseUrl = `${environment.apiBaseUrl}v1/team`;

    constructor() {
        super();
    }

    override findAll(searchParam: any): Observable<PageResponse<TeamItem>> {
        return this.wrapRequest(
            this.http.get<any>(this.baseUrl, {
                headers: this.headers,
                params: convertToHttpParams(searchParam)
            }).pipe(
                map((response: any) => {
                    const data = response?.response || response;
                    return {
                        content: (Array.isArray(data?.content) ? data.content : (Array.isArray(data) ? data : [])).map((team: any) => this.mapTeam(team)),
                        page: data?.page || {
                            number: searchParam?.page || 0,
                            size: searchParam?.size || 10,
                            totalElements: Array.isArray(data?.content) ? data.content.length : 0,
                            totalPages: 1
                        }
                    } as PageResponse<TeamItem>;
                })
            )
        );
    }

    override find(id: string): Observable<TeamItem> {
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
                map((response: any) => {
                    const data = response?.response || response;
                    return this.mapTeam(data);
                })
            )
        );
    }

    override create(data: Partial<TeamItem>): Observable<TeamItem> {
        const payload: TeamUpsertPayload = {
            uniqueId: data.uniqueId,
            name: data.name || '',
            description: data.description,
            isActive: data.isActive ?? true,
            members: data.members?.map(member => ({
                uniqueId: member.uniqueId,
                interviewerUniqueId: member.interviewerUniqueId,
                isActive: member.isActive
            })) || []
        };
        return this.createOrUpdate(payload);
    }

    override update(id: string, data: Partial<TeamItem>): Observable<TeamItem> {
        const payload: TeamUpsertPayload = {
            uniqueId: id,
            name: data.name || '',
            description: data.description,
            isActive: data.isActive ?? true,
            members: data.members?.map(member => ({
                uniqueId: member.uniqueId,
                interviewerUniqueId: member.interviewerUniqueId,
                isActive: member.isActive
            })) || []
        };
        return this.createOrUpdate(payload);
    }

    override delete(id: string): Observable<void> {
        return this.wrapRequest(
            this.http.delete<void>(`${this.baseUrl}/${id}`, { headers: this.headers })
        );
    }

    override getTeams(params?: any): Observable<TeamItem[]> {
        const httpParams = convertToHttpParams(params || {});
        return this.wrapRequest(
            this.http.get<any>(this.baseUrl, { headers: this.headers, params: httpParams }).pipe(
                map((response: any) => {
                    const data = response?.response || response;
                    const content = Array.isArray(data) ? data : (data?.content || []);
                    return content.map((team: any) => this.mapTeam(team));
                })
            )
        );
    }

    override getTeamMembers(teamUniqueId: string, params?: any): Observable<TeamMemberItem[]> {
        const httpParams = convertToHttpParams(params || {});
        return this.wrapRequest(
            this.http.get<any>(`${this.baseUrl}/${teamUniqueId}`, { headers: this.headers, params: httpParams }).pipe(
                map((response: any) => {
                    const data = response?.response || response;
                    const content = Array.isArray(data?.content)
                        ? data.content
                        : Array.isArray(data)
                            ? data
                            : [];

                    return content.map((member: any) => ({
                        uniqueId: member.uniqueId || member.id || member.teamMemberUniqueId,
                        teamUniqueId: member.teamUniqueId || teamUniqueId,
                        teamName: member.teamName,
                        interviewerUniqueId: member.interviewerUniqueId,
                        interviewerName: member.interviewerName,
                        isActive: !!member.isActive
                    } as TeamMemberItem));
                })
            )
        );
    }

    override getInterviewers(params?: any): Observable<TeamInterviewerItem[]> {
        const httpParams = convertToHttpParams(params || {});
        return this.wrapRequest(
            this.http.get<any>(`${environment.apiBaseUrl}v1/interviewer`, { headers: this.headers, params: httpParams }).pipe(
                map((response: any) => {
                    const data = response?.response || response;
                    const list = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.content)
                            ? data.content
                            : data
                                ? [data]
                                : [];

                    return list.map((item: any) => ({
                        uniqueId: item.interviewerUniqueId || item.uniqueId,
                        interviewerName: item.interviewerName,
                        interviewType: item.interviewType,
                        isRepeatWeekly: item.isRepeatWeekly,
                        isActive: !!item.isActive,
                        skills: Array.isArray(item.skills) ? item.skills : []
                    } as TeamInterviewerItem));
                })
            )
        );
    }

    override createOrUpdate(payload: TeamUpsertPayload): Observable<TeamItem> {
        return this.wrapRequest(
            this.http.post<any>(`${this.baseUrl}/create-or-update`, payload, { headers: this.headers }).pipe(
                map((response: any) => this.mapTeam(response?.response || response))
            )
        );
    }

    private mapTeam(team: any): TeamItem {
        const actualTeam = Array.isArray(team) ? team[0] || {} : team || {};
        const members = Array.isArray(actualTeam?.members) ? actualTeam.members : [];
        return {
            uniqueId: actualTeam?.uniqueId || actualTeam?.teamUniqueId || actualTeam?.id || actualTeam?.teamId || actualTeam?._id,
            name: actualTeam?.name || actualTeam?.teamName,
            description: actualTeam?.description,
            isActive: actualTeam?.isActive ?? actualTeam?.active,
            members: members.map((member: any) => ({
                uniqueId: member.uniqueId || member.id || member.teamMemberUniqueId,
                teamUniqueId: member.teamUniqueId,
                teamName: member.teamName,
                interviewerUniqueId: member.interviewerUniqueId,
                interviewerName: member.interviewerName,
                isActive: !!member.isActive
            } as TeamMemberItem))
        };
    }
}
